'use client'

import { motion } from 'framer-motion'
import { useEffect, useRef, useState } from 'react'
import { cn } from '@/utils/styles/cn'

/**
 * Rolling-digit "odometer" number — generalised from the superuser control-center KPI card
 * (agent-panel-manager-overview-card's AnimatedKpiValue / RollingDigit). Each digit is a vertical
 * 0–9 stack sprung to `y: -{digit}em` (Trade-Republic / Revolut style). Two behaviours:
 *   • Intro roll — on first scroll-into-view every digit rolls up from 0 → its value.
 *   • Live roll — when `value` later changes (e.g. polled platform stats) only the affected digits
 *     re-roll to the new number.
 *
 * SEO / a11y: before it enters the viewport — and with JS disabled — it renders the real formatted
 * value as plain text; the wheel wrapper always carries the true value in `aria-label`. Non-digit
 * characters (the de-DE "." thousands separator) render as static spans. Client leaf: keep the
 * surrounding stat card server-rendered.
 */

const SPRING = { type: 'spring', mass: 0.6, stiffness: 90, damping: 18 } as const

const formatDeDe = (value: number) => value.toLocaleString('de-DE')

export function VisionAnimatedNumber({
    value,
    format = formatDeDe,
    className,
}: {
    value: number
    format?: (value: number) => string
    className?: string
}) {
    const ref = useRef<HTMLSpanElement>(null)
    const [active, setActive] = useState(false)

    useEffect(() => {
        const element = ref.current
        if (!element) return
        if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return // keep plain value

        const observer = new IntersectionObserver(
            ([entry]) => {
                if (entry.isIntersecting) {
                    setActive(true)
                    observer.disconnect()
                }
            },
            { threshold: 0.6 }
        )
        observer.observe(element)
        return () => observer.disconnect()
    }, [])

    const formatted = format(value)

    return (
        <span ref={ref} className={cn('inline-flex items-baseline tabular-nums', className)} aria-label={formatted}>
            {active ? <Odometer text={formatted} /> : <span aria-hidden>{formatted}</span>}
        </span>
    )
}

function Odometer({ text }: { text: string }) {
    const chars = text.split('')
    const length = chars.length
    return (
        <>
            {chars.map((char, index) => {
                // key by position-from-right so a value change only re-rolls the digits that moved
                const fromRight = length - index - 1
                if (/\d/.test(char)) return <RollingDigit key={`d-${fromRight}`} digit={Number(char)} />
                return (
                    <span key={`s-${fromRight}-${char}`} aria-hidden>
                        {char}
                    </span>
                )
            })}
        </>
    )
}

function RollingDigit({ digit }: { digit: number }) {
    return (
        <span
            className="relative inline-block overflow-hidden align-baseline"
            style={{ height: '1em', lineHeight: 1 }}
            aria-hidden
        >
            <motion.span className="block" initial={{ y: 0 }} animate={{ y: `-${digit}em` }} transition={SPRING}>
                {Array.from({ length: 10 }, (_, i) => (
                    <span key={i} className="block" style={{ height: '1em', lineHeight: 1 }}>
                        {i}
                    </span>
                ))}
            </motion.span>
        </span>
    )
}
